import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { api, ApiError } from "../lib/api.js";
import { addPasskey, deviceCanHoldPasskey, wasCancelled } from "../lib/passkey.js";

/**
 * "Use Face ID next time?" — asked once, right after an email-link sign-in,
 * on a device that can hold a passkey and holds none for this person yet.
 *
 * "Not now" is remembered on this device and the question does not come back;
 * settings is where to change your mind. Ignoring it means it asks again after
 * the next email sign-in, which is the moment it is most worth asking.
 */

const DECLINED_KEY = "frankie.passkey-offer.declined";

function declinedHere(): boolean {
  try {
    return window.localStorage.getItem(DECLINED_KEY) === "1";
  } catch {
    return false;
  }
}

function declineHere(): void {
  try {
    window.localStorage.setItem(DECLINED_KEY, "1");
  } catch {
    // Private browsing can refuse storage. The offer just asks again next time.
  }
}

type State = "hidden" | "asking" | "busy" | "done";

export function PasskeyOffer(): React.JSX.Element | null {
  const location = useLocation();
  const navigate = useNavigate();
  const [state, setState] = useState<State>("hidden");
  const [error, setError] = useState<string | null>(null);

  const fromLink = new URLSearchParams(location.search).get("signin") === "link";

  useEffect(() => {
    if (!fromLink) return;
    // Off the address bar at once, so a reload or a bookmark doesn't ask again.
    void navigate(location.pathname, { replace: true });

    if (declinedHere()) return;

    void (async () => {
      if (!(await deviceCanHoldPasskey())) return;
      const existing = await api.passkeys().catch(() => null);
      if (existing?.length === 0) setState("asking");
    })();
  }, [fromLink, location.pathname, navigate]);

  if (state === "hidden") return null;

  async function accept(): Promise<void> {
    setState("busy");
    setError(null);
    try {
      await addPasskey();
      setState("done");
      setTimeout(() => setState("hidden"), 2400);
    } catch (e) {
      if (wasCancelled(e)) {
        setState("asking");
        return;
      }
      setError(e instanceof ApiError ? e.message : "That didn't work. Try again from Settings.");
      setState("asking");
    }
  }

  function decline(): void {
    declineHere();
    setState("hidden");
  }

  return (
    <div className="toast offer" role="dialog" aria-label="Use Face ID next time">
      {state === "done" ? (
        <span>Done. Face ID next time.</span>
      ) : (
        <>
          <span>
            Use Face ID next time?
            {error !== null && <span className="offer__error">{error}</span>}
          </span>
          <button type="button" className="offer__no" onClick={decline}>
            Not now
          </button>
          <button
            type="button"
            className="toast__undo"
            disabled={state === "busy"}
            onClick={() => void accept()}
          >
            {state === "busy" ? "Waiting…" : "Set it up"}
          </button>
        </>
      )}
    </div>
  );
}
